import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import FilterPanel from "@/components/opportunities/FilterPanel";
import OpportunityTable, { BOARD_COLS } from "@/components/opportunities/OpportunityTable";
import { ColumnsMenu, useVisibleCols } from "@/components/ColumnsMenu";
import { BoardScanPanel } from "@/components/opportunities/BoardScanPanel";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Download, MagnifyingGlass, ArrowsClockwise, Lightning } from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  loadPersistedFilter, persistFilter, loadLocks, saveLocks, DEFAULT_LOCKS, TOGGLE_KEYS,
} from "@/lib/filterStore";

const PAGE_SIZES = [50, 100, 250, 500, 1000];

const DEFAULT_FILTER = {
  min_roi: null, min_profit: null, min_sales_rank: null, max_sales_rank: null,
  min_seller_count: null, min_score: null,
  themes: null, retired_only: false, active_only: false,
  in_stock_only: false, exclude_hazmat: false, live_only: false,
  exclude_unlisted: false, previously_purchased: false, asin_status: null,
  search: "", sort_by: "opportunity_score", sort_dir: "desc",
  page: 1, page_size: Number(localStorage.getItem("board_page_size")) || 50,
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState(() => loadPersistedFilter(DEFAULT_FILTER));
  const [locks, setLocks] = useState(() => loadLocks());
  const [data, setData] = useState({ rows: [], total: 0, summary: {} });
  const [loading, setLoading] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [savedName, setSavedName] = useState("");
  const [savedFilters, setSavedFilters] = useState([]);
  const [looking, setLooking] = useState(false);

  // Persist whenever the filter changes (so lock-toggling later restores values).
  useEffect(() => { persistFilter(filter); }, [filter]);
  useEffect(() => { saveLocks(locks); }, [locks]);

  const updateLocks = (next) => setLocks(next);

  const load = async (f = filter) => {
    setLoading(true);
    try {
      const d = await api.searchOpportunities(f);
      setData(d);
    } catch (e) {
      toast.error("Failed to load opportunities");
    }
    setLoading(false);
  };

  useEffect(() => { load(filter); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => { api.listSavedFilters().then(setSavedFilters).catch(() => {}); }, []);

  // Deals-style column visibility + row selection
  const [visibleCols, toggleCol, isVisible] = useVisibleCols("board_visible_cols", BOARD_COLS);
  const [selected, setSelected] = useState(new Set());
  const toggleRow = (sn) => setSelected(prev => { const n = new Set(prev); if (n.has(sn)) n.delete(sn); else n.add(sn); return n; });
  const toggleAll = () => setSelected(prev => {
    const all = data.rows.map(r => r.set_number);
    return all.length && all.every(sn => prev.has(sn)) ? new Set() : new Set(all);
  });
  useEffect(() => { setSelected(new Set()); }, [filter.page, filter.themes, filter.search, filter.asin_status]);

  const onSort = (key) => {
    const dir = filter.sort_by === key ? (filter.sort_dir === "desc" ? "asc" : "desc") : "desc";
    setFilter({ ...filter, sort_by: key, sort_dir: dir, page: 1 });
  };

  const onLiveLookup = async () => {
    const q = (filter.search || "").trim();
    if (!q) {
      toast.error("Type a set number first");
      return;
    }
    setLooking(true);
    const tid = toast.loading(`Fetching live data for ${q}…`);
    try {
      const d = await api.liveLookup(q);
      const ds = d.opportunity?.data_source;
      const listed = d.opportunity?.amazon_listed;
      if (listed === false) {
        toast.warning(`${q} is not listed on Amazon — only BrickLink data is available`, { id: tid });
      } else if (ds === "live") {
        toast.success(`✓ Live Keepa + BrickLink data for ${q}`, { id: tid });
      } else if (ds === "partial") {
        toast.success(`Partial live data fetched for ${q} (one source unavailable)`, { id: tid });
      } else {
        toast.warning(`Set ${q} fetched, but couldn't verify live prices (showing demo). Check Settings → API Diagnostics.`, { id: tid });
      }
      navigate(`/set/${d.opportunity.set_number}`);
    } catch (e) {
      toast.error(`Live lookup failed for ${q}`, { id: tid });
    }
    setLooking(false);
  };

  const onExport = async () => {
    try {
      const blob = await api.exportCsv(filter);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "opportunities.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch { toast.error("Export failed"); }
  };

  const onSaveFilter = async () => {
    if (!savedName.trim()) return;
    const cleanFilter = { ...filter, page: 1 };
    try {
      await api.createSavedFilter({ name: savedName, filter: cleanFilter });
      setSavedFilters(await api.listSavedFilters());
      setSaveOpen(false);
      setSavedName("");
      toast.success(`Saved "${savedName}"`);
    } catch { toast.error("Save failed"); }
  };

  const onResetFilter = () => {
    // Honor locks: keep locked sections, reset everything else.
    const next = { ...DEFAULT_FILTER };
    if (locks.toggles) {
      for (const k of TOGGLE_KEYS) next[k] = filter[k];
    }
    if (locks.themes) {
      next.themes = filter.themes;
    }
    setFilter(next);
  };

  const summary = data.summary || {};
  const pages = Math.ceil((data.total || 0) / filter.page_size) || 1;
  const setPageSize = (n) => {
    try { localStorage.setItem("board_page_size", String(n)); } catch {}
    setFilter({ ...filter, page_size: n, page: 1 });
  };

  return (
    <div className="flex h-[calc(100vh-86px)]">
      <FilterPanel
        value={filter}
        onChange={setFilter}
        onReset={onResetFilter}
        onSave={() => setSaveOpen(true)}
        locks={locks}
        onLocksChange={updateLocks}
      />

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header bar */}
        <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-[#E4E4E0] bg-[#FFFFFF]">
          <h1 className="font-head text-xl font-black uppercase tracking-tighter mr-2" data-testid="dashboard-title">Opportunity Board</h1>

          <div className="relative flex-1 min-w-[200px] max-w-md">
            <MagnifyingGlass size={14} className="absolute left-2 top-2.5 text-gray-500" />
            <Input placeholder="Search set # or name…" value={filter.search || ""}
              onChange={e => setFilter({ ...filter, search: e.target.value, page: 1 })}
              className="h-8 pl-8 bg-[#F6F6F3] border-[#E4E4E0] font-mono text-xs" data-testid="dashboard-search" />
          </div>

          <Button onClick={onLiveLookup} disabled={looking} className="h-8 rounded-sm border border-[#E3000B] bg-transparent text-[#E3000B] hover:bg-[#E3000B]/10 font-bold uppercase tracking-wider text-xs disabled:opacity-50" data-testid="dashboard-live-lookup">
            <Lightning size={12} weight="fill" className="mr-1" /> {looking ? "FETCHING…" : "LIVE LOOKUP"}
          </Button>

          {savedFilters.length > 0 && (
            <select onChange={(e) => {
              const sf = savedFilters.find(x => x.id === e.target.value);
              if (sf) setFilter({ ...DEFAULT_FILTER, ...sf.filter });
            }} className="h-8 bg-[#F6F6F3] border border-[#E4E4E0] text-xs font-mono px-2 text-gray-800 rounded-sm" data-testid="saved-filters-select">
              <option value="">Saved filters…</option>
              {savedFilters.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}

          <Button onClick={() => load(filter)} variant="outline" className="h-8 rounded-sm border-[#E4E4E0] bg-[#F6F6F3] text-xs font-mono hover:border-[#E3000B] hover:text-[#E3000B]" data-testid="dashboard-refresh">
            <ArrowsClockwise size={12} className="mr-1" /> REFRESH
          </Button>
          <Button onClick={onExport} className="h-8 rounded-sm bg-[#E3000B] text-white font-bold uppercase tracking-wider text-xs hover:bg-[#E6B800]" data-testid="dashboard-export">
            <Download size={12} className="mr-1" /> EXPORT CSV
          </Button>
          <ColumnsMenu allCols={BOARD_COLS} visible={visibleCols} toggle={toggleCol} testIdPrefix="board" className="h-8" />
          <div className="inline-flex items-center border border-[#E4E4E0] rounded-sm bg-[#F6F6F3] h-8 overflow-hidden" data-testid="page-size-picker" title="Rows per page">
            <span className="px-2 font-mono text-[10px] uppercase text-gray-500 border-r border-[#E4E4E0] h-full inline-flex items-center">SHOW</span>
            {PAGE_SIZES.map(n => (
              <button key={n} onClick={() => setPageSize(n)} data-testid={`page-size-${n}`}
                className={`px-2.5 h-full font-mono text-[11px] font-bold transition-colors ${
                  filter.page_size === n ? "bg-[#E3000B] text-white" : "text-gray-600 hover:text-black hover:bg-[#EFEFEA]"
                }`}>{n}</button>
            ))}
          </div>
        </div>

        {/* Scans — Deals-style bulk ASIN / Amazon / BrickLink for the current theme scope */}
        <BoardScanPanel themes={filter.themes} onDataChanged={() => load(filter)}
          selected={selected} onClearSelection={() => setSelected(new Set())} />

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-b border-[#E4E4E0]">
          <Kpi label="Total Results" value={data.total} testId="kpi-total" />
          <Kpi label="Avg ROI" value={`${(summary.avg_roi || 0).toFixed(1)}%`} tone={summary.avg_roi >= 0 ? "pos" : "neg"} testId="kpi-avg-roi" />
          <Kpi label="Total Projected Profit" value={`$${(summary.sum_profit || 0).toFixed(0)}`} tone="pos" testId="kpi-profit" />
          <Kpi label="Profitable Count" value={summary.count_positive || 0} tone="accent" testId="kpi-positive" />
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto p-4">
          {filter.previously_purchased && (
            <div className="mb-3 px-3 py-2 border border-[#E3000B]/40 bg-[#E3000B]/[0.06] rounded-sm flex items-center gap-2 text-[11px] font-mono" data-testid="history-view-notice">
              <span className="text-[#E3000B] font-bold uppercase tracking-wider">History View:</span>
              <span className="text-gray-600">Showing every set from your archive — min ROI / profit / score filters are ignored so you see all past buys (including currently-underwater ones).</span>
            </div>
          )}
          <OpportunityTable rows={data.rows} sortBy={filter.sort_by} sortDir={filter.sort_dir} onSort={onSort} loading={loading}
            selected={selected} onToggle={toggleRow} onToggleAll={toggleAll} isVisible={isVisible} />

          {/* Pagination */}
          <div className="flex items-center justify-between mt-3 text-xs font-mono text-gray-600">
            <div data-testid="pagination-info">Showing page {filter.page} of {pages} · {data.total} rows</div>
            <div className="flex gap-2">
              <Button disabled={filter.page <= 1} onClick={() => setFilter({ ...filter, page: filter.page - 1 })} variant="outline" className="h-7 rounded-sm border-[#E4E4E0] bg-[#F6F6F3] font-mono text-xs disabled:opacity-30" data-testid="page-prev">PREV</Button>
              <Button disabled={filter.page >= pages} onClick={() => setFilter({ ...filter, page: filter.page + 1 })} variant="outline" className="h-7 rounded-sm border-[#E4E4E0] bg-[#F6F6F3] font-mono text-xs disabled:opacity-30" data-testid="page-next">NEXT</Button>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#E4E4E0] rounded-sm text-gray-900">
          <DialogHeader><DialogTitle className="font-head uppercase tracking-tight">Save Filter</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs font-mono uppercase text-gray-500">Name</Label>
            <Input value={savedName} onChange={e => setSavedName(e.target.value)} className="h-9 bg-[#F6F6F3] border-[#E4E4E0] font-mono" data-testid="save-filter-name" />
          </div>
          <DialogFooter>
            <Button onClick={onSaveFilter} className="bg-[#E3000B] text-white hover:bg-[#E6B800] font-bold uppercase rounded-sm" data-testid="save-filter-confirm">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ label, value, tone, testId }) {
  const tones = {
    pos: "text-[#00A850]",
    neg: "text-[#FF3333]",
    accent: "text-[#E3000B]",
  };
  const cls = tones[tone] || "text-gray-900";
  return (
    <div className="border-r border-[#E4E4E0] px-4 py-3" data-testid={testId}>
      <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500 mb-1">{label}</div>
      <div className={`font-mono text-2xl font-bold tracking-tighter ${cls}`}>{value}</div>
    </div>
  );
}
